import { lazy, Suspense } from 'react';
import { useTranslation } from 'react-i18next';

import { Tooltip } from '@chakra-ui/react';
import { KeyboardKey } from '@this+that/utils';

import { useCreditsBalanceRow } from './useCreditsBalanceRow';
import { CreditsCardVariant } from '../../../../../../utils/credits/creditsBalanceMath';

const BuyCreditsModal = lazy(() =>
  import('../../../../../credits/BuyCreditsModal/BuyCreditsModal').then(
    (m) => ({
      default: m.BuyCreditsModal,
    }),
  ),
);

type RowTone = 'neutral' | 'low' | 'danger';

function resolveTone(
  variant: CreditsCardVariant | undefined,
  isNoWallet: boolean,
): RowTone {
  if (
    isNoWallet ||
    variant === undefined ||
    variant === CreditsCardVariant.HEALTHY
  ) {
    return 'neutral';
  }
  if (variant === CreditsCardVariant.LOW) {
    return 'low';
  }
  return 'danger';
}

const ROW_BG_BY_TONE: Record<RowTone, string> = {
  neutral: 'bg-semantic-surface-2',
  low: 'bg-[var(--color-primary-100)]',
  danger: 'bg-semantic-bg-danger-hover',
};

const DOT_BY_TONE: Record<RowTone, string> = {
  neutral: 'bg-semantic-bg-accent',
  low: 'bg-semantic-status-warning',
  danger: 'bg-semantic-status-error',
};

const AMOUNT_BY_TONE: Record<RowTone, string> = {
  neutral: 'text-semantic-text-primary',
  low: 'text-[var(--color-primary-800)]',
  danger: 'text-semantic-text-danger',
};

const stylesheet = {
  row: (tone: RowTone) =>
    `flex items-center gap-2 border-t border-semantic-border px-3 py-2 cursor-pointer ${ROW_BG_BY_TONE[tone]}`,
  dot: (tone: RowTone) => `h-2 w-2 flex-none rounded-full ${DOT_BY_TONE[tone]}`,
  skeletonDot:
    'h-2 w-2 flex-none rounded-full bg-semantic-bg-subtle animate-pulse',
  balanceGroup:
    'flex min-w-0 cursor-pointer items-center gap-2 border-none bg-transparent p-0 text-left',
  amount: (tone: RowTone) =>
    `text-[13px] font-bold tabular-nums ${AMOUNT_BY_TONE[tone]}`,
  amountSkeleton:
    'h-[13px] w-10 rounded-semantic bg-semantic-bg-subtle animate-pulse',
  unit: 'text-[13px] text-semantic-text-secondary',
  noWalletText: 'truncate text-[13px] text-semantic-text-secondary',
  spacer: 'flex-1',
  addLink:
    'text-[13px] font-bold text-semantic-text-accent hover:text-semantic-emerald-hover',
};

export function CreditsBalanceRow() {
  const { t } = useTranslation();
  const {
    isEnabled,
    loading,
    derived,
    amountLabel,
    totalLabel,
    amountCount,
    isNoWallet,
    showAddLink,
    isBuyModalOpen,
    openBuyModal,
    closeBuyModal,
    walletKind,
    goToCreditsPage,
  } = useCreditsBalanceRow();

  if (!isEnabled) {
    return null;
  }

  const isLoadingInitial = loading && !derived;
  const tone = resolveTone(derived?.variant, isNoWallet);

  return (
    <div
      role="button"
      tabIndex={0}
      className={stylesheet.row(tone)}
      data-testid="credits-balance-row"
      onClick={goToCreditsPage}
      onKeyDown={(event) => {
        if (event.key === KeyboardKey.ENTER) {
          goToCreditsPage();
        } else if (event.key === KeyboardKey.SPACE) {
          event.preventDefault();
          goToCreditsPage();
        }
      }}
    >
      {isLoadingInitial ? (
        <>
          <span className={stylesheet.skeletonDot} aria-hidden="true" />
          <span className={stylesheet.amountSkeleton} aria-hidden="true" />
        </>
      ) : isNoWallet ? (
        <>
          <span className={stylesheet.dot(tone)} />
          <span className={stylesheet.noWalletText}>
            {t('credits.balanceRow.noWallet')}
          </span>
        </>
      ) : (
        <Tooltip
          label={t('credits.balanceRow.tooltip', {
            balance: amountLabel,
            total: totalLabel,
          })}
          hasArrow={true}
          bg="gray.800"
          color="white"
        >
          <button type="button" className={stylesheet.balanceGroup}>
            <span className={stylesheet.dot(tone)} />
            <span className={stylesheet.amount(tone)}>{amountLabel}</span>
            <span className={stylesheet.unit}>
              {t('credits.unit', { count: amountCount })}
            </span>
          </button>
        </Tooltip>
      )}

      <span className={stylesheet.spacer} />

      {showAddLink && (
        <button
          type="button"
          className={stylesheet.addLink}
          onClick={(event) => {
            event.stopPropagation();
            openBuyModal();
          }}
        >
          {t('credits.balanceRow.addLink')}
        </button>
      )}

      {isBuyModalOpen && (
        <Suspense fallback={null}>
          <BuyCreditsModal
            isOpen={isBuyModalOpen}
            onClose={closeBuyModal}
            walletKind={walletKind}
          />
        </Suspense>
      )}
    </div>
  );
}
